import type { FBO, DoubleFBO } from "../types/fluid-types.js"

// Global buffers for the full-screen quad
let blitVertexBuffer: WebGLBuffer | null = null
let blitIndexBuffer: WebGLBuffer | null = null

/**
 * Initializes the vertex buffer for blitting.
 */
export function initBlitBuffer(
	gl: WebGLRenderingContext | WebGL2RenderingContext
): void {
	if (!blitVertexBuffer) {
		// Create vertex buffer for a quad
		blitVertexBuffer = gl.createBuffer()
		gl.bindBuffer(gl.ARRAY_BUFFER, blitVertexBuffer)
		gl.bufferData(
			gl.ARRAY_BUFFER,
			new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]),
			gl.STATIC_DRAW
		)

		// Create index buffer for the quad
		blitIndexBuffer = gl.createBuffer()
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, blitIndexBuffer)
		gl.bufferData(
			gl.ELEMENT_ARRAY_BUFFER,
			new Uint16Array([0, 1, 2, 0, 2, 3]),
			gl.STATIC_DRAW
		)
	}
}

/**
 * Draws a full-screen quad to trigger the fragment shader on a target FBO.
 */
export function blit(
	gl: WebGLRenderingContext | WebGL2RenderingContext,
	target: FBO | null
): void {
	if (!blitVertexBuffer) {
		initBlitBuffer(gl)
	}

	gl.bindBuffer(gl.ARRAY_BUFFER, blitVertexBuffer)
	gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, blitIndexBuffer)
	gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
	gl.enableVertexAttribArray(0)

	if (target == null) {
		gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight)
		gl.bindFramebuffer(gl.FRAMEBUFFER, null)
	} else {
		gl.viewport(0, 0, target.width, target.height)
		gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo)
	}
	gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0)
}

/**
 * Creates a single framebuffer object.
 */
export function createFBO(
	gl: WebGLRenderingContext | WebGL2RenderingContext,
	w: number,
	h: number,
	internalFormat: number,
	format: number,
	type: number,
	param: number
): FBO {
	gl.activeTexture(gl.TEXTURE0)
	const texture = gl.createTexture()!
	gl.bindTexture(gl.TEXTURE_2D, texture)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
	gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null)

	const fbo = gl.createFramebuffer()!
	gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
	gl.framebufferTexture2D(
		gl.FRAMEBUFFER,
		gl.COLOR_ATTACHMENT0,
		gl.TEXTURE_2D,
		texture,
		0
	)
	gl.viewport(0, 0, w, h)
	gl.clear(gl.COLOR_BUFFER_BIT)

	const texelSizeX = 1.0 / w
	const texelSizeY = 1.0 / h

	return {
		fbo,
		texture,
		width: w,
		height: h,
		texelSizeX,
		texelSizeY,
		attach(id: number): number {
			gl.activeTexture(gl.TEXTURE0 + id)
			gl.bindTexture(gl.TEXTURE_2D, texture)
			return id
		},
	}
}

/**
 * Creates a double framebuffer for ping-pong rendering.
 */
export function createDoubleFBO(
	gl: WebGLRenderingContext | WebGL2RenderingContext,
	w: number,
	h: number,
	internalFormat: number,
	format: number,
	type: number,
	param: number
): DoubleFBO {
	let fbo1 = createFBO(gl, w, h, internalFormat, format, type, param)
	let fbo2 = createFBO(gl, w, h, internalFormat, format, type, param)

	return {
		read: fbo1,
		write: fbo2,
		swap(): void {
			const temp = fbo1
			fbo1 = fbo2
			fbo2 = temp
			this.read = fbo1
			this.write = fbo2
		},
	}
}

/**
 * Calculates resolution based on a target value and aspect ratio.
 */
export function getResolution(
	gl: WebGLRenderingContext | WebGL2RenderingContext,
	resolution: number
): { width: number; height: number } {
	let aspectRatio = gl.drawingBufferWidth / gl.drawingBufferHeight
	if (aspectRatio < 1) aspectRatio = 1.0 / aspectRatio

	const min = Math.round(resolution)
	const max = Math.round(resolution * aspectRatio)

	if (gl.drawingBufferWidth > gl.drawingBufferHeight) {
		return { width: max, height: min }
	} else {
		return { width: min, height: max }
	}
}
